import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiSend } from "@/lib/api-error";
import {
  EMPTY_SERVICE_BASELINE,
  loadServiceSavedBaseline,
  resolveServiceSavePrice,
  typedOldClientPriceAsBase,
  type ServiceSavedBaseline,
} from "@/lib/service-price-save";
import { useServicePriceLimits } from "@/hooks/useServicePriceLimits";
import { usePricingConfig } from "@/hooks/usePricingConfig";
import { toast } from "@/lib/toast";
import { DEFAULT_BOOKING_HORIZON_DAYS } from "@/lib/booking-horizon";
import { isMatchingProfileReady } from "@/lib/matching/profile-readiness";
import { therapistContractQueryOptions } from "@/api-queries/documents";
import { EMPTY_MATCHING_PROFILE } from "@/components/matching/MatchingProfileEditor";
import type { BillingIdentityValue } from "@/components/enrollment/BillingIdentityFields";
import type { PayoutMethod } from "@/types";
import type { TherapistMatchingProfile } from "@/types/matching";
import type { TherapistContractStatus } from "@/lib/documents/therapist-contract";
import {
  defaultDays, scheduleRowsToDays, daysToScheduleRows, validateDaySegments, validateBilling, billingPayload,
  computeOnboardingState, applyServiceTemplate,
  clampOnboardingStepIndex, nextOnboardingStepIndex, onboardingStepOrder, type ServiceTemplateKey,
} from "./onboarding-logic";
import type { BookingSettingsState, DayAvailability, ScheduleRow, ServiceFormState, StepKey } from "./types";

interface MeResponse {
  id: string;
  userName?: string;
  officeAddress?: string | null;
  isMigrated?: boolean;
  termsAcceptedAt?: string | null;
  contract?: TherapistContractStatus;
  isVolunteer?: boolean;
  payoutMethod?: PayoutMethod;
  businessName?: string | null;
  businessAddress?: string | null;
  businessTaxId?: string | null;
  businessVatId?: string | null;
  personalAddress?: string | null;
  personalTaxId?: string | null;
  bankName?: string | null;
  bankAccount?: string | null;
}


/**
 * State, seeding and per-step saving for the therapist onboarding wizard. The route
 * (`/dashboard/therapist/onboarding`) only renders; every server write lives here.
 */
export function useOnboardingWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [seeded, setSeeded] = useState(false);

  // Captured once at seed time so the wizard keeps a stable step order even after
  // the agreement is accepted mid-flow (the /me refetch would otherwise drop it).
  const [showContract, setShowContract] = useState(false);
  const [showBillingTerms, setShowBillingTerms] = useState(false);
  const [isMigrated, setIsMigrated] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [agreedToContract, setAgreedToContract] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isVolunteer, setIsVolunteer] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod>("invoice");
  const [billing, setBilling] = useState<BillingIdentityValue>({});

  const [days, setDays] = useState<DayAvailability[]>(defaultDays);
  const [booking, setBooking] = useState<BookingSettingsState>({ bufferMinutes: 10, minBookingLeadDays: 2, bookingHorizonDays: DEFAULT_BOOKING_HORIZON_DAYS, acceptingNewClients: true });
  const [officeAddress, setOfficeAddress] = useState("");
  const [service, setService] = useState<ServiceFormState>(() => applyServiceTemplate("individual"));
  const [selectedTemplate, setSelectedTemplate] = useState<ServiceTemplateKey | null>("individual");
  const [otherServiceCount, setOtherServiceCount] = useState(0);
  // Base (bez PDV-a) baseline + raw gross the existing service was loaded with; an unchanged price
  // stays allowed and is persisted byte-identical to what was loaded (see resolveServiceSavePrice).
  const [serviceSavedBaseline, setServiceSavedBaseline] = useState<ServiceSavedBaseline>(EMPTY_SERVICE_BASELINE);
  // Set after the first save attempt with the old client price typed as the base, so a
  // deliberate second click still goes through.
  const [oldPriceWarned, setOldPriceWarned] = useState(false);
  const servicePriceLimits = useServicePriceLimits();
  const pricingConfig = usePricingConfig();
  const [matching, setMatching] = useState<TherapistMatchingProfile>(EMPTY_MATCHING_PROFILE);
  const [completion, setCompletion] = useState<Record<StepKey, boolean>>({ welcome: true, agreement: true, review: false, availability: false, services: false, matching: false, finish: false });

  // Review step: which imported card is open for editing, plus the values it opened
  // with so "Odustani" restores them instead of saving a half-edited form.
  const [availabilityEdit, setAvailabilityEdit] = useState<{ days: DayAvailability[]; booking: BookingSettingsState } | null>(null);
  const [serviceEdit, setServiceEdit] = useState<{ service: ServiceFormState; officeAddress: string } | null>(null);

  const showAgreement = showContract || showBillingTerms;
  const STEP_ORDER = useMemo(() => onboardingStepOrder({ showAgreement, reviewMode }), [showAgreement, reviewMode]);

  const { data: me } = useQuery({ queryKey: ["therapist", "profile", "me"], queryFn: () => apiGet<MeResponse>("/api/therapist/me", "Greška pri učitavanju profila") });
  const therapistId = me?.id;
  const contractQuery = useQuery({ ...therapistContractQueryOptions(), enabled: showContract });

  useQuery({
    queryKey: ["onboarding", "seed"],
    // Wait for the real rate config: the service price loaded here is converted
    // through it, and a guessed rate must never leak into a form the therapist edits.
    enabled: !!therapistId && !seeded && !!pricingConfig.config,
    queryFn: async () => {
      const config = pricingConfig.config;
      if (!config) throw new Error("Cijene nisu učitane.");
      const [avail, bk, svc, match] = await Promise.all([
        apiGet<{ schedule: ScheduleRow[] }>("/api/therapist/availability", "Greška pri učitavanju dostupnosti"),
        apiGet<BookingSettingsState & { slotDurationMinutes: number }>("/api/therapist/booking-settings", "Greška pri učitavanju postavki"),
        apiGet<{ services: (ServiceFormState & { listPrice?: number })[] }>(`/api/services?therapistId=${therapistId}&all=1`, "Greška pri učitavanju usluga"),
        apiGet<{ profile: TherapistMatchingProfile; isEmpty: boolean }>("/api/me/therapist/matching-profile", "Greška pri učitavanju matching profila"),
      ]);

      const schedule = avail.schedule ?? [];
      if (schedule.length > 0) setDays(scheduleRowsToDays(schedule));
      setOfficeAddress(me?.officeAddress ?? "");
      setBooking({ bufferMinutes: bk.bufferMinutes, minBookingLeadDays: bk.minBookingLeadDays, bookingHorizonDays: bk.bookingHorizonDays, acceptingNewClients: bk.acceptingNewClients });

      const services = svc.services ?? [];
      const first = services[0];
      if (first) {
        // Stored price is the gross client-facing price; the step edits the service
        // price without VAT, so convert on load.
        // `listPrice`, ne `price`: API u `price` vraća cijenu pomnoženu koeficijentom
        // zemlje terapeuta, pa bi netaknuto polje tu uvećanu cijenu upisalo u bazu.
        const baseline = loadServiceSavedBaseline(first.listPrice ?? null, config);
        setService({ id: first.id, name: first.name, description: first.description, durationMinutes: first.durationMinutes, price: baseline.base, modality: first.modality, maxMembers: first.maxMembers ?? null });
        setServiceSavedBaseline(baseline);
        setSelectedTemplate(null);
      }
      setOtherServiceCount(Math.max(0, services.length - 1));
      if (match.profile) setMatching(match.profile);

      const contractRequired = Boolean(me?.contract?.required);
      const migrated = Boolean(me?.isMigrated);
      const billingTermsPending = migrated && !me?.termsAcceptedAt;
      // Imported schedule + service are confirmed on one review screen instead of two forms.
      const review = migrated && schedule.length > 0 && services.length > 0;
      setShowContract(contractRequired);
      setShowBillingTerms(billingTermsPending);
      setIsMigrated(migrated);
      setReviewMode(review);
      setIsVolunteer(Boolean(me?.isVolunteer));
      setPayoutMethod(me?.payoutMethod ?? "invoice");
      setBilling({
        businessName: me?.businessName ?? undefined,
        businessAddress: me?.businessAddress ?? undefined,
        businessTaxId: me?.businessTaxId ?? undefined,
        businessVatId: me?.businessVatId ?? undefined,
        personalAddress: me?.personalAddress ?? undefined,
        personalTaxId: me?.personalTaxId ?? undefined,
        bankName: me?.bankName ?? undefined,
        bankAccount: me?.bankAccount ?? undefined,
      });

      const state = computeOnboardingState({
        scheduleCount: schedule.length, serviceCount: services.length,
        matchingReady: isMatchingProfileReady(match.profile),
        showContract: contractRequired, contractAccepted: Boolean(me?.contract?.acceptedAt),
        showBillingTerms: billingTermsPending, termsAccepted: false,
        reviewMode: review,
      });
      setCompletion(state.completion);
      setIndex(onboardingStepOrder({ showAgreement: contractRequired || billingTermsPending, reviewMode: review }).indexOf(state.landingStep));
      setSeeded(true);
      return state;
    },
  });

  const stepKey = STEP_ORDER[index]!;
  const requiredDone = STEP_ORDER.every((k) => k === "welcome" || k === "finish" || completion[k]);
  const goTo = (i: number, state = completion) => setIndex(clampOnboardingStepIndex(i, state, STEP_ORDER));
  // Always the next step: imported data is reviewed, never skipped.
  const goNext = (state = completion) => setIndex(nextOnboardingStepIndex(index, state, STEP_ORDER));
  const contractPending = showContract && !contractQuery.data?.acceptedAt;
  const termsPending = showBillingTerms && !termsAccepted;

  function markCompletion(patch: Partial<Record<StepKey, boolean>>): Record<StepKey, boolean> {
    const next = { ...completion, ...patch };
    setCompletion(next);
    return next;
  }

  async function saveAgreement(): Promise<Record<StepKey, boolean> | null> {
    if (showContract && !contractQuery.data) { toast.error("Ugovor nije učitan. Pokušaj ponovo."); return null; }
    if (termsPending && !isVolunteer) {
      const err = validateBilling(payoutMethod, billing);
      if (err) { toast.error(err); return null; }
    }
    if (contractPending && !agreedToContract) { toast.error("Označi da prihvataš ugovor."); return null; }
    if (termsPending && !agreedToTerms) { toast.error("Označi da prihvataš uslove korištenja."); return null; }

    if (termsPending) {
      await apiSend("/api/therapist/onboarding/billing-terms", "POST", { payoutMethod, agreedToTerms: true, ...billingPayload(payoutMethod, billing) }, "Greška pri spremanju načina naplate");
      setTermsAccepted(true);
    }
    if (contractPending) {
      try {
        await apiSend("/api/therapist/contract/accept", "POST", { versionId: contractQuery.data!.versionId }, "Greška pri prihvatanju ugovora");
      } catch (err) {
        // Admin je u međuvremenu objavio novu verziju: učitaj svježi tekst i traži novo čitanje.
        await queryClient.invalidateQueries({ queryKey: ["therapist", "contract"] });
        setAgreedToContract(false);
        throw err;
      }
      await queryClient.invalidateQueries({ queryKey: ["therapist", "contract"] });
    }
    await queryClient.invalidateQueries({ queryKey: ["therapist", "profile", "me"] });
    return markCompletion({ agreement: true });
  }

  async function saveAvailability(): Promise<Record<StepKey, boolean> | null> {
    for (const d of days) {
      if (d.enabled) {
        const err = validateDaySegments(d.segments);
        if (err) { toast.error(err); return null; }
      }
    }
    const rows = daysToScheduleRows(days);
    if (rows.length === 0) { toast.error("Uključi bar jedan radni dan."); return null; }
    await apiSend("/api/therapist/availability", "PUT", rows, "Greška pri spremanju radnog vremena");
    await apiSend("/api/therapist/booking-settings", "PATCH", booking, "Greška pri spremanju postavki");
    return markCompletion({ availability: true });
  }

  async function saveService(): Promise<Record<StepKey, boolean> | null> {
    if (service.name.trim().length < 1) { toast.error("Unesi naziv usluge."); return null; }
    if (!service.price || service.price <= 0) { toast.error("Unesi cijenu veću od 0."); return null; }
    if (!pricingConfig.config) {
      toast.error("Cijene se još učitavaju, pokušaj ponovo za par sekundi.");
      return null;
    }
    // The step captures the service price without VAT; persist the gross client price.
    // An unchanged price is persisted as the original stored gross, not recomputed, since
    // the round trip isn't perfectly idempotent (see resolveServiceSavePrice).
    if (typedOldClientPriceAsBase(service.price, serviceSavedBaseline) && !oldPriceWarned) {
      setOldPriceWarned(true);
      toast.error("Provjeri cijenu: upisao/la si dosadašnju cijenu sa PDV-om, pa bi klijent plaćao više. Ako to zaista želiš, klikni ponovo.");
      return null;
    }
    const resolution = resolveServiceSavePrice(service.price, serviceSavedBaseline, servicePriceLimits, pricingConfig.config);
    if ("error" in resolution) { toast.error(resolution.error); return null; }
    const grossPrice = resolution.price;
    // Services offered in person require an office address (server enforces this too).
    if (service.modality !== "online") {
      if (officeAddress.trim().length === 0) { toast.error("Unesi adresu ordinacije za seanse uživo."); return null; }
      if (therapistId) await apiSend(`/api/therapists/${therapistId}`, "PATCH", { officeAddress: officeAddress.trim() }, "Greška pri spremanju adrese");
    }
    const payload = { name: service.name.trim(), description: service.description.trim() || service.name.trim(), durationMinutes: service.durationMinutes, price: grossPrice, modality: service.modality, maxMembers: service.maxMembers };
    if (service.id) {
      await apiSend(`/api/services/${service.id}`, "PATCH", payload, "Greška pri spremanju usluge");
    } else {
      const created = await apiSend<{ id: string }>("/api/services", "POST", payload, "Greška pri kreiranju usluge");
      setService((s) => ({ ...s, id: created.id }));
    }
    setServiceSavedBaseline({ base: service.price, gross: grossPrice });
    return markCompletion({ services: true });
  }

  async function saveReview(): Promise<Record<StepKey, boolean> | null> {
    let next = completion;
    if (availabilityEdit) {
      const saved = await saveAvailability();
      if (!saved) return null;
      next = saved;
      setAvailabilityEdit(null);
    }
    if (serviceEdit) {
      const saved = await saveService();
      if (!saved) return null;
      next = saved;
      setServiceEdit(null);
    }
    const done = { ...next, review: next.availability && next.services };
    setCompletion(done);
    return done;
  }

  async function saveMatching(): Promise<Record<StepKey, boolean> | null> {
    if (!isMatchingProfileReady(matching)) {
      toast.error("Označi bar jednu glavnu oblast rada i jednu grupu klijenata.");
      return null;
    }
    await apiSend("/api/me/therapist/matching-profile", "PUT", matching, "Greška pri spremanju oblasti rada");
    await queryClient.invalidateQueries({ queryKey: ["therapist", "matching-profile", "me"] });
    return markCompletion({ matching: true });
  }

  const SAVERS: Partial<Record<StepKey, () => Promise<Record<StepKey, boolean> | null>>> = {
    agreement: saveAgreement,
    review: saveReview,
    availability: saveAvailability,
    services: saveService,
    matching: saveMatching,
  };

  async function handlePrimary() {
    setSaving(true);
    try {
      if (stepKey === "finish") {
        if (!requiredDone) { toast.error("Dovrši sve korake prije završetka."); return; }
        await apiSend("/api/therapist/onboarding/complete", "POST", {}, "Greška pri završetku");
        await queryClient.invalidateQueries({ queryKey: ["therapist", "profile", "me"] });
        navigate({ to: "/dashboard/therapist" });
        return;
      }
      const save = SAVERS[stepKey];
      const next = save ? await save() : completion;
      if (next) goNext(next);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Došlo je do greške. Pokušaj ponovo.");
    } finally {
      setSaving(false);
    }
  }

  return {
    index, stepKey, STEP_ORDER, completion, saving, requiredDone, goTo, handlePrimary,
    isMigrated, showAgreement, showContract, showBillingTerms, reviewMode,
    contractQuery, contractPending, termsPending,
    agreedToContract, setAgreedToContract, termsAccepted, agreedToTerms, setAgreedToTerms,
    isVolunteer, payoutMethod, setPayoutMethod, billing, setBilling,
    days, setDays, booking, setBooking, officeAddress, setOfficeAddress,
    service, setService, selectedTemplate, setSelectedTemplate, otherServiceCount, serviceSavedBaseline,
    pricingConfig, matching, setMatching,
    availabilityEdit, setAvailabilityEdit, serviceEdit, setServiceEdit,
  };
}
